import React, { useState, useEffect } from 'react';
import { useAuth0 } from "@auth0/auth0-react";
import { useUserContext } from '../../contexts/UserContext';
import { useContentfulContext } from '../../contexts/ContentfulContext';
import { ResendVerificationEmail, getAdminUserProfile, adminUpdateUserProfile, adminDeactivateUser, updateOrganizationRole } from '../../utils/APIs';
import Loader from '../UI/Loader/Loader';
import OrganizationRole from '../UI/OrganizationRole';
import { SecondaryButton } from "../UI";
import Input from '../UI/Input';
import { toast } from 'react-toastify';
import PrimaryModal from '../UI/Modal/PrimaryModal';
import UpdatePassword from '../ManageUser/UpdatePassword';
import ConfirmModal from '../UI/Modal/confirmModal';
import SuccessModal from '../ManageUser/SuccessModal';


const UpdateUserModal = ({ setSelectedAction, selectedUser, getAllOrgUsers }) => {
    const { getAccessTokenSilently } = useAuth0();
    const { userData, roles } = useUserContext();
    const { content } = useContentfulContext();
    const [loading, setLoading] = useState(false);
    const [user, setUser] = useState(null);
    const [userName, setUserName] = useState(selectedUser?.name);
    const [orgRoles, setOrgRoles] = useState([]);

    const [countdown, setCountdown] = useState(content.SEND_VERIFICATION_SUCCESS_MODAL.timeout);
    const [isCountdownActive, setIsCountdownActive] = useState(false);

    const [showUpdatePassword, setShowUpdatePassword] = useState(false);
    const [openConfirmDeactivate, setOpenConfirmDeactivate] = useState(false)

    const [isUserBlocked, setIsUserBlocked] = useState(selectedUser?.blocked)

    const startCountdown = async () => {
        if (user) {
            await ResendVerificationEmail(user.user_id)
        }
        setIsCountdownActive(true);
        setCountdown(content.SEND_VERIFICATION_SUCCESS_MODAL.timeout);

        const countdownInterval = setInterval(() => {
            setCountdown((prevCountdown) => {
                if (prevCountdown === 1) {
                    clearInterval(countdownInterval);
                    setIsCountdownActive(false);
                    setSelectedAction("");
                    return 0;
                }
                return prevCountdown - 1;
            });
        }, 1000);
    };

    const fetchUser = async () => {
        try {
            setLoading(true);
            const res = await getAdminUserProfile(userData?.token, selectedUser.user_id, userData?.selectedOrganization?.org_token);
            if (res.status) {
                setUser(res.data);
                setOrgRoles(res.data.organizations.map(org => ({
                    org_token: org.org_token,
                    org_name: org.name,
                    role: org.role[0],
                })));
                setIsUserBlocked(res.data?.blocked)
                setUserName(res.data.name)
            }
        } catch (error) {
            console.error('Failed to fetch user data:', error);
            toast.error('Failed to load user data.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (countdown === 0) {
            setIsCountdownActive(false);
        }
    }, [countdown]);

    useEffect(() => {
        fetchUser();
    }, []);

    const updateOrgRole = (orgToken, newRole) => {
        setOrgRoles(prevRoles =>
            prevRoles.map(orgRole =>
                orgRole.org_token === orgToken ? { ...orgRole, role: newRole } : orgRole
            )
        );
    };
    useEffect(() => {
        // Re-initialize roles if needed when returning from modal
        if (user) {
            setOrgRoles(user.organizations.map(org => ({
                org_token: org.org_token,
                org_name: org.name,
                role: org.role[0]
            })));
        }
    }, [showUpdatePassword, openConfirmDeactivate]);

    const handleSaveChanges = async () => {
        try {
            setLoading(true);
            const token = await getAccessTokenSilently();

            for (const org of orgRoles) {
                const currentRole = roles.find(r => r.name === org.role);
                const previousRole = user?.organizations.find(o => o.org_token === org.org_token)?.role[0]?.name;

                // Only update if the roles are different
                if (currentRole.name !== previousRole) {
                    const deletedRole = roles.find(r => r.name === previousRole);
                    const data = {
                        organizationId: org.org_token,
                        userId: user?.user_id,
                        roleId: currentRole.name,
                        deletedRoleId: deletedRole?.name,
                    };

                    await updateOrganizationRole(token, data);
                    toast.success('Organization roles updated successfully.');
                }
            }

            // Only update user name if it has changed
            if (userName !== user?.name) {
                const result = await adminUpdateUserProfile(userData?.token, user?.user_id, { name: userName }, userData?.selectedOrganization?.org_token);
                setUserName(result.data.name);
                if (result.status) {
                    setTimeout(() => {
                        getAllOrgUsers()
                        setLoading(false);
                    }, 2000)
                }
            }

        } catch (error) {
            console.error('Error updating roles:', error);
            toast.error('Failed to update roles.');
        } finally {
            setLoading(false);
        }
    };

    const handleDeactivate = async () => {
        try {
            setLoading(true)
            const res = await adminDeactivateUser(userData.token, user.user_id, userData?.selectedOrganization?.org_token)
            if (res.status) {
                fetchUser()
                setOpenConfirmDeactivate(false)
                setLoading(false)
                toast.success(res.message);
            }
        } catch (error) {
            setLoading(false)
            console.log("error", error)
            toast.error("There is some error to activate user, Try after some time.");
        }
    }

    const handleActivateAccount = async () => {
        setLoading(true)
        const result = await adminUpdateUserProfile(userData.token, user.user_id, {
            blocked: false
        }, userData?.selectedOrganization?.org_token)
        if (result.status) {
            fetchUser()
            setOpenConfirmDeactivate(false)
            setLoading(false)
            toast.success("User activated succesfully")
        } else {
            setLoading(false)
            toast.error(result.message)
        }

    }

    return (
        <>
            {
                loading && <Loader />
            }
            {isCountdownActive && <SuccessModal countdown={countdown} trigger={() => setSelectedAction("")} description={content.SEND_VERIFICATION_SUCCESS_MODAL.description} cta_text={content.SEND_VERIFICATION_SUCCESS_MODAL.close_cta_text} title={content.SEND_VERIFICATION_SUCCESS_MODAL.title} />}

            {!isCountdownActive && <PrimaryModal title={content.UPDATE_USER_MODAL_CONTENT.title} hideModal={() => setSelectedAction("")}>

                {showUpdatePassword && <UpdatePassword setShowUpdatePassword={setShowUpdatePassword} selectedUser={selectedUser} />}

                {openConfirmDeactivate && (
                    <ConfirmModal
                        title={`Confirm to ${isUserBlocked ? 'Activate' : 'Deactivate'} this Account`}
                        hideModal={setOpenConfirmDeactivate}
                        handleConfirmation={isUserBlocked ? handleActivateAccount : handleDeactivate}
                    />
                )}

                {(!showUpdatePassword && !openConfirmDeactivate) && (<div className="p-6 space-y-6">
                    <Input
                        id="email"
                        name="email"
                        label={content.UPDATE_USER_MODAL_CONTENT.input.email}
                        disabled
                        value={selectedUser.email}
                        className="mb-4"
                    />
                    <div className='p-4 border border-gray-2 rounded-lg'>
                        <Input
                            id="name"
                            name="name"
                            label={content.UPDATE_USER_MODAL_CONTENT.input.name}
                            value={userName}
                            onChange={(e) => setUserName(e.target.value)}
                            className="mb-4"
                        />
                        <div className='relative mb-4 text-left'>
                            <label className="stride-form-label">{content.UPDATE_USER_MODAL_CONTENT.input.organization_role}</label>
                            <p className='text-xs text-stride-muted mb-3'>
                                {
                                    user?.organizations.length > 0
                                        ? content.UPDATE_USER_MODAL_CONTENT.change_account_permission
                                        : content.UPDATE_USER_MODAL_CONTENT.assign_org
                                }
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                                {
                                    orgRoles.map((org, index) => (
                                        <div key={org.org_token} className='mb-4'>
                                            <h3 className="text-sm font-semibold text-stride-heading mb-1.5">{org?.org_name}</h3>
                                            <OrganizationRole
                                                organizationRole={org?.role?.name || org?.role}
                                                roles={roles}
                                                callback={(newRole) => updateOrgRole(org.org_token, newRole)}
                                            />
                                        </div>
                                    ))
                                }
                            </div>
                        </div>
                        <div className='flex justify-end items-center'>
                            <button
                                type="button"
                                // className={`text-white bg-primary hover:bg-primary focus:ring-4 focus:outline-none focus:ring-primary-2 font-medium rounded-lg text-sm px-5 py-2.5 text-center ${!userValid && "disabled:border disabled:border-gray-2 disabled:bg-gray-3 disabled:text-black"}`}
                                className={`text-white bg-primary hover:bg-primary focus:ring-4 focus:outline-none focus:ring-primary-2 font-medium rounded-lg text-sm px-5 py-2.5 text-center`}
                                // disabled={!userValid}
                                onClick={handleSaveChanges}
                            >
                                Save
                            </button>
                        </div>
                    </div>
                    <div className='mb-4'>
                        <h2 className="text-xl font-bold leading-tight tracking-tight text-black text-left mb-2">{content.UPDATE_USER_MODAL_CONTENT.instruction_text.update_password.title}</h2>
                        <p className='text-base leading-tight tracking-tight text-black mb-4'>{content.UPDATE_USER_MODAL_CONTENT.instruction_text.update_password.description}</p>
                        <div className='flex justify-start space-x-4'>
                            <button className="text-primary bg-white focus:outline-none rounded-lg border border-primary text-sm font-medium px-5 py-2.5" type="button" onClick={() => { setShowUpdatePassword(true) }}>{content.UPDATE_USER_MODAL_CONTENT.instruction_text.update_password.cta_text}</button>
                        </div>
                    </div>
                    {!selectedUser.email_verified && <div className='mb-4'>
                        <h2 className="text-xl font-bold leading-tight tracking-tight text-black text-left mb-2">{content.UPDATE_USER_MODAL_CONTENT.instruction_text.send_verification_link.title}</h2>
                        <p className='text-base leading-tight tracking-tight text-black mb-4'>{content.UPDATE_USER_MODAL_CONTENT.instruction_text.send_verification_link.description}</p>
                        <div className='flex justify-start space-x-4'>
                            <button className="text-primary bg-white focus:outline-none rounded-lg border border-primary text-sm font-medium px-5 py-2.5" type="button" onClick={() => {
                                startCountdown();
                            }}>{content.UPDATE_USER_MODAL_CONTENT.instruction_text.send_verification_link.cta_text}</button>
                        </div>
                    </div>}
                    <div className='relative'>
                        <h2 className="text-xl font-bold leading-tight tracking-tight text-black text-left mb-2">{!isUserBlocked ? content.UPDATE_USER_MODAL_CONTENT.instruction_text.deactivate_account.title : content.UPDATE_USER_MODAL_CONTENT.instruction_text.activate_account.title}</h2>
                        <p className='text-base leading-tight tracking-tight text-black mb-4'>{!isUserBlocked ? content.UPDATE_USER_MODAL_CONTENT.instruction_text.deactivate_account.description : ""}</p>
                        <button className="text-danger bg-white focus:outline-none rounded-lg border border-danger text-sm font-medium px-5 py-2.5" type="button" onClick={() => { setOpenConfirmDeactivate(true) }}>{!isUserBlocked ? content.UPDATE_USER_MODAL_CONTENT.instruction_text.deactivate_account.cta_text : content.UPDATE_USER_MODAL_CONTENT.instruction_text.activate_account.cta_text}</button>
                    </div>
                    <div className='flex justify-end'>
                        <SecondaryButton handleClick={() => {
                            setSelectedAction("");
                        }}>{content.UPDATE_USER_MODAL_CONTENT.cancel_cta_text}</SecondaryButton>
                    </div>
                </div>)}
            </PrimaryModal>}
        </>
    )
}

export default UpdateUserModal;